import {
  Anchor,
  Button,
  InfoModal,
  Input,
  Toast,
  CopyClipboardSVG,
} from "@newsela/angelou";
import PropTypes from "prop-types";

import globalToasterRef from "@/base/globalToasterRef";
import { MONOLITH_ORIGIN } from "@/monolithIntegration/monolithIntegration.constants";
import useGenerateClassCode from "@/classrooms/useGenerateClassCode";
import {
  copyButtonCss,
  copyButtonIconCss,
  copyLabelCss,
  copyUnderInputCss,
  fieldWrapperCss,
  generateButtonCss,
  generateContainerCss,
  generateErrorCss,
  modalContentWrapperCss,
  modalFooterCss,
  modalRootCss,
  modalscrollableBodyCss,
  sectionDividerCss,
} from "./ClassroomInvitationModal.style";

const propTypes = {
  /**
   * Pass a classroom object returned from a call to the Classroom API.
   */
  classroom: PropTypes.object,
  /**
   * Props passed directly to the InfoModal component. See the Angelou InfoModal for more information.
   */
  classroomInvitationModalProps: PropTypes.object,
  /**
   * Props passed directly to the Button in the top right corner of the modal. See the Angelou Button for more information.
   */
  closeButtonProps: PropTypes.object,
};

const EVENT_TOAST_SUCCESS_COPIED = {
  showIcon: true,
  statusColor: Toast.statusColor.primary,
  children: "Copied to clipboard",
};

const JoinCodeSection = ({
  isNativeClassroom,
  hasCode,
  classCode,
  isPending,
  isError,
  generateCode,
  quickjoinLink,
  onClickCopy,
  sharedButtonProps,
}) => {
  if (!isNativeClassroom) {
    return null;
  }

  if (!hasCode) {
    return (
      <div css={generateContainerCss}>
        <p>No class code has been generated yet.</p>
        <Button
          flavor={Button.flavors.primary}
          onClick={() => generateCode()}
          disabled={isPending}
          ariaProps={{
            "aria-label": "Generate a class code",
          }}
          __cssFor={{ root: generateButtonCss }}
        >
          {isPending ? "Generating..." : "Generate Code"}
        </Button>
        {isError && (
          <p role="alert" css={generateErrorCss}>
            Failed to generate code. Please try again.
          </p>
        )}
      </div>
    );
  }

  return (
    <>
      <div css={fieldWrapperCss}>
        <Input
          label="Share class link"
          value={quickjoinLink}
          __cssFor={{
            label: copyLabelCss,
            underInput: copyUnderInputCss,
          }}
          disabled
        />
        <Button
          ariaProps={{
            "aria-label": "Copy class link to clipboard",
          }}
          onClick={() => onClickCopy(quickjoinLink)}
          {...sharedButtonProps}
        />
      </div>
      <div css={sectionDividerCss}>
        <strong>OR</strong>
        <hr />
      </div>
      <div css={fieldWrapperCss}>
        <Input
          label="Share class code"
          value={classCode}
          __cssFor={{
            label: copyLabelCss,
            underInput: copyUnderInputCss,
          }}
          disabled
        />
        <Button
          ariaProps={{
            "aria-label": "Copy class code to clipboard",
          }}
          onClick={() => onClickCopy(classCode)}
          {...sharedButtonProps}
        />
      </div>
    </>
  );
};

const ClassroomInvitationModal = ({
  classroom,
  classroomInvitationModalProps,
  closeButtonProps,
}) => {
  const isNativeClassroom = classroom?.provider?.internal_name === "native";

  const { generateCode, isPending, isError, classCode } = useGenerateClassCode({
    classroomUniqueId: classroom?.unique_id,
    existingClassCode: classroom?.class_code,
  });
  const hasCode = Boolean(classCode);

  const sharedButtonProps = {
    flavor: Button.flavors.polarMuted,
    icon: {
      SvgComponent: CopyClipboardSVG,
    },
    __cssFor: {
      root: copyButtonCss,
      Icon: copyButtonIconCss,
    },
  };

  const onClickCopy = (text) => {
    navigator.clipboard.writeText(text);
    globalToasterRef.current.displayToast(EVENT_TOAST_SUCCESS_COPIED);
  };

  const quickjoinLink = `${MONOLITH_ORIGIN}/quickjoin/#/${classCode}`;

  return (
    <InfoModal
      labelId="InviteStudents"
      title={`Invite your students to ${classroom?.name}`}
      __cssFor={{
        root: modalRootCss,
        modalContentWrapper: modalContentWrapperCss,
        scrollableBody: modalscrollableBodyCss,
        footer: modalFooterCss,
      }}
      closeButtonProps={closeButtonProps}
      {...classroomInvitationModalProps}
    >
      <JoinCodeSection
        isNativeClassroom={isNativeClassroom}
        hasCode={hasCode}
        classCode={classCode}
        isPending={isPending}
        isError={isError}
        generateCode={generateCode}
        quickjoinLink={quickjoinLink}
        onClickCopy={onClickCopy}
        sharedButtonProps={sharedButtonProps}
      />
      <span>
        For step-by-step instructions, print and share the&nbsp;
        <Anchor
          href={`${MONOLITH_ORIGIN}/classrooms/${classroom?.id}/quickstart`}
          isInline
        >
          {classroom?.name} Quick Start Guide
        </Anchor>
      </span>
    </InfoModal>
  );
};

ClassroomInvitationModal.propTypes = propTypes;
export default ClassroomInvitationModal;
